'use client';

import Link from 'next/link';
import { useMemo } from 'react';
import { MdArrowDropDown } from 'react-icons/md';
import { useWallet } from '@solana/wallet-adapter-react';

import { useRecoilValue } from 'recoil';
import { selectedProjectDataState, userActiveBidsState, userRefundedBidsState } from '@/lib/state';

import { lamportsToToken } from '@/lib/utils';
import ClientOnly from '@/components/ClientOnly';

export default function ActiveUserBidsDropdown({ pid }) {
  const activeBids = useRecoilValue(userActiveBidsState);
  const refundedBids = useRecoilValue(userRefundedBidsState);
  const { token } = useRecoilValue(selectedProjectDataState);
  const { publicKey } = useWallet();

  const displayBids = useMemo(
    () =>
      (activeBids &&
        activeBids[pid] &&
        Object.entries(
          activeBids[pid]
            .map(({ lamports, qty, decimals }) => [lamportsToToken(decimals, lamports), qty])
            .reduce((acc, [sol, qty]) => {
              if (!acc[sol]) {
                acc[sol] = 0;
              }
              acc[sol] += Number(qty);
              return acc;
            }, {})
        ).sort(([a], [b]) => b - a)) ||
      [],
    [activeBids, pid]
  );

  const numRefunds = useMemo(
    () =>
      (refundedBids &&
        refundedBids[pid] &&
        refundedBids[pid]
          .map(({ lamports, qty, decimals }) => [lamportsToToken(decimals, lamports), qty])
          .reduce((acc, [, qty]) => {
            acc += Number(qty);
            return acc;
          }, 0)) ||
      0,
    [refundedBids, pid]
  );

  const totalNumActive = useMemo(
    () => displayBids?.reduce((acc, [_, qty]) => acc + Number(qty || 0), 0) || 0,
    [displayBids]
  );

  if (!publicKey) {
    return (
      <div className="border border-neutral w-full flex p-4 justify-center font-light">
        <p>[ Connect wallet to see Live Winning Bids ]</p>
      </div>
    );
  }

  if (!((activeBids && pid && activeBids[pid]) || (pid && refundedBids && refundedBids[pid]))) {
    return (
      <div className="border border-neutral w-full flex p-4 justify-center font-light">
        <p>[ No live winning bids for this wallet ]</p>
      </div>
    );
  }

  if (totalNumActive === 0 && numRefunds === 0) {
    return (
      <div className="border border-neutral w-full flex p-4 justify-center font-light">
        <p>[ No live winning bids for this wallet ]</p>
      </div>
    );
  }

  return (
    <ClientOnly>
      <div className="border-t border-neutral" />
      <div className="dropdown z-10">
        <label tabIndex={0} className="w-full">
          <div className="flex w-full items-center justify-between">
            {totalNumActive === 0 && numRefunds > 0 && (
              <div className="flex p-4 space-x-2 text-warning text-lg">
                <p>⚪️</p>
                <p>No Bids ({numRefunds} Refunded)</p>
              </div>
            )}
            {totalNumActive > 0 && (
              <div className="flex p-4 space-x-2 text-lg">
                <p>🟢</p>
                <p>{totalNumActive} Live Winning Bids</p>
              </div>
            )}
            <div className="mr-2">
              <MdArrowDropDown size="2em" />
            </div>
          </div>
        </label>
        <ul
          tabIndex={0}
          className="dropdown-content menu w-full shadow space-y-1 bg-base-200 border border-neutral"
        >
          {displayBids?.map(([sol, qty]) => (
            <li key={`${pid}-active-${sol}-${qty}`}>
              <Link href="/bids" className="flex w-full font-mono items-center justify-between">
                {sol} {token}
                <div>{qty} bids</div>
              </Link>
            </li>
          ))}
          <li>
            <Link
              href="/bids#refunds"
              className="flex w-full font-mono items-center justify-between text-neutral-400"
            >
              Refunded
              <div>{numRefunds || 0} bids</div>
            </Link>
          </li>
        </ul>
      </div>
    </ClientOnly>
  );
}
